import { Close } from "@mui/icons-material";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/joy";
import Color from "color";
import { format } from "date-fns";
import { uniq, uniqBy } from "lodash";
import { memo, useEffect, useMemo, useState } from "react";

import { Route } from "@/core/types";
import { useCurrentVenueMapsByUuidQuery } from "@/hooks/venues";
import formatLifetime from "@/utils/formatLifetime";
import { getTraitIcon, getTraitLabel, TraitValue } from "@/utils/traits";

import Avatar from "../Avatar";
import { MapSwitcher } from "../forms/MapPositionInput";
import MapComponent from "../Map/Map";
import MapRoutePinSvg from "../Map/MapRoutePinSvg";
import { RouteWithMap } from "../Map/MapsList";
import { RouteCircle } from "../RouteCircle";
import { RouteNameText } from "../RoutesTable/RouteName";
import RoutePin from "../RoutesTable/RoutePin";
import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";
import { useArchiveQueryContextRoutes } from "./ArchiveRoutesQueryContext";
import { useArchiveUiStore } from "./ArchiveUiStore";

const RoutesMap = ({ routes }: { routes: Route[] }) => {
  const { data: mapsByUuid } = useCurrentVenueMapsByUuidQuery();
  const [selectedMapUuid, setSelectedMapUuid] = useState<string | null>(null);
  const routesByMap = useMemo(() => {
    const result = {} as Record<string, RouteWithMap[]>;
    routes.forEach((route) => {
      if (!route.map || !route.mapPosition) {
        return;
      }
      if (!result[route.map]) {
        result[route.map] = [];
      }
      result[route.map].push(route as RouteWithMap);
    });
    return result;
  }, [routes]);

  useEffect(() => {
    if (selectedMapUuid === null || !routesByMap[selectedMapUuid]) {
      setSelectedMapUuid(Object.keys(routesByMap)[0]);
    }
  }, [routesByMap, selectedMapUuid]);

  const mapsCount = Object.keys(routesByMap).length;

  if (!mapsByUuid || !selectedMapUuid) {
    return null;
  }

  return (
    <Box
      sx={{
        height: "390px",
        backgroundColor: "neutral.900",
        position: "relative",
      }}
    >
      <MapComponent
        map={mapsByUuid[selectedMapUuid]}
        mapObjects={routesByMap[selectedMapUuid] || []}
        bboxPadding={[10, 10, 10, 10]}
        maxZoom={10}
        idAccessor={(route) => route.uuid}
        zoomToBbox={false}
        brushEnabled={false}
        selectedIds={new Set()}
        onSetSelectedIds={() => null}
        renderMapObject={(route, transform) => {
          return (
            <g transform={`translate(${route.mapPosition.x}, ${route.mapPosition.y})`}>
              <g transform={`scale(${1 / Math.min(transform.k, 4)})`}>
                <MapRoutePinSvg route={route} disableTagAlert />
              </g>
            </g>
          );
        }}
      />
      {mapsCount > 1 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            height: 0,
            position: "absolute",
            right: 8,
            bottom: 36,
          }}
        >
          <MapSwitcher
            map={selectedMapUuid}
            onChange={(mapUuid) => {
              if (mapUuid) {
                setSelectedMapUuid(mapUuid);
              }
            }}
            mapsByUuid={mapsByUuid}
          />
        </Box>
      ) : null}
    </Box>
  );
};

const RouteHeader = ({ routes }: { routes?: Route[] }) => {
  const route = routes?.length === 1 ? routes[0] : undefined;
  let bgColor: string;
  let fgColor: string;

  if (route) {
    const colors = (route.colors || "#FFFFFF").split(":");
    bgColor = colors[0];
    fgColor = Color(bgColor).isDark() ? "#FFFFFF" : "#000000";
  } else {
    bgColor = "#FFFFFF";
    fgColor = "#000000";
  }

  const setDisplayRouteSidebar = useArchiveUiStore((state) => state.setDisplayRouteSidebar);

  return (
    <Box
      sx={{
        height: 42,
        backgroundColor: bgColor,
        color: fgColor,
        padding: 1,
        fontSize: 16,
        fontWeight: "bold",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        border: "1px solid",
        borderColor: "#00000033",
        borderRadius: "16px 16px 0 0",
      }}
    >
      {route ? (
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: fgColor,
          }}
        >
          {route ? <RouteNameText route={route} /> : "?"}
        </Typography>
      ) : null}
      {!route && routes ? (
        <Stack
          direction="row"
          spacing={routes.length > 10 ? -1.5 : 0.5}
          sx={{ overflow: "hidden" }}
        >
          {routes.map((route, _index) => (
            <Box sx={{ flexShrink: 0 }} key={route.uuid}>
              <RoutePin route={route} />
            </Box>
          ))}
        </Stack>
      ) : null}
      <IconButton
        size="sm"
        sx={{
          marginBottom: -0.5,
          borderRadius: 100,
          "&:hover": {
            backgroundColor: fgColor + "33",
          },
        }}
        onClick={() => setDisplayRouteSidebar(false)}
      >
        <Close htmlColor={fgColor} />
      </IconButton>
    </Box>
  );
};

export default function ArchiveRoutesSidebar({ hidden = false }: { hidden?: boolean }) {
  const selectedRouteUuids = useRoutesTableStore((state) => state.selectedRouteUuids);
  const allRoutes = useArchiveQueryContextRoutes();
  const routes = useMemo(() => {
    const routesByUuid = new Map(allRoutes?.map((route) => [route.uuid, route]));
    const result: Route[] = [];
    for (const uuid of selectedRouteUuids) {
      const route = routesByUuid.get(uuid);
      if (route) {
        result.push(route);
      }
    }
    return result;
  }, [allRoutes, selectedRouteUuids]);

  return (
    <Box
      sx={{
        height: "100%",
        display: hidden ? "none" : "flex",
        flexDirection: "column",
        borderTopLeftRadius: 16,
        overflow: "hidden",
        borderLeft: "1px solid",
        borderColor: "neutral.200",
      }}
    >
      <RouteHeader routes={routes} />
      <RoutesMap routes={routes} />
      <RouteDetails routes={routes} />
    </Box>
  );
}

const TraitsDisplay = ({
  type,
  values,
}: {
  type: "styles" | "skills" | "holds";
  values: TraitValue[];
}) => {
  return (
    <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
      {values.map((value) => {
        const TraitIcon = getTraitIcon(type, value);
        return (
          <Stack key={value} direction="row" gap={0.5} alignItems="center">
            <Chip
              startDecorator={<TraitIcon size="sm" />}
              variant="soft"
              color="neutral"
              sx={{ fontWeight: "bold", color: "neutral.900" }}
            >
              {getTraitLabel(type, value)}
            </Chip>
          </Stack>
        );
      })}
    </Stack>
  );
};

const DetailsSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <Box sx={{ padding: 1 }}>
      <Typography>{title}</Typography>
      {children}
    </Box>
  );
};

const getAscentOptions = (route: Route) => {
  if (route.autoBelay) {
    return "Auto-belay";
  }
  if (route.canLead) {
    return "Toprope + Lead";
  }
  return "Toprope";
};

function getTruncatedDisplay<T extends string | number | undefined | null>(items: T[]) {
  const itemsToDisplay = new Set(items.filter((x) => x !== undefined && x !== null));
  if (itemsToDisplay.size === 0) {
    return "–";
  }
  return `${Array.from(itemsToDisplay).slice(0, 5).join(", ")}${
    itemsToDisplay.size > 5 ? "..." : ""
  }`;
}

function singleOrPlaceholder<T extends string | number | undefined | null>(
  items: T[],
  ignoreNullish = true,
) {
  if (ignoreNullish) {
    items = items.filter((x) => x !== undefined && x !== null);
  }
  const set = new Set(items);
  if (set.size > 1) {
    return (
      <Typography textColor="neutral.600" fontWeight="normal" fontSize={13}>
        (multiple values)
      </Typography>
    );
  } else {
    return items[0] ?? "–";
  }
}

const RouteDetails = memo(({ routes }: { routes: Route[] }) => {
  const hasRouteWithDisplayNumber = routes.some(
    (route) =>
      route.circuit?.routeDisplay === "NUMBER" ||
      (route.circuit?.routeDisplay === undefined && route.climbType !== "SPORT"),
  );

  const hasRouteWithCircuit = routes.some((r) => r.circuit);

  const routeNumbers = routes.map((route) => route.circuitNumber).filter((x) => !!x);
  const routeCircuitNames = routes.map((route) => route.circuit?.name);
  const routeZoneNames = routes.map((route) => route.zone?.name);
  const routeGradeValues = routes.map((route) => route.gradeValue ?? "?");
  const startDatesFormatted = routes.map((route) =>
    route.startTime ? format(route.startTime, "do MMM yyyy") : null,
  );
  const endDatesFormatted = routes.map((route) =>
    route.endTime ? format(route.endTime, "do MMM yyyy") : null,
  );
  const lifetimesFormatted = routes.map((route) =>
    formatLifetime(
      route.endTime && route.startTime
        ? new Date(route.endTime).getTime() - new Date(route.startTime).getTime()
        : Infinity,
    ),
  );
  const distinctSetters = uniqBy(routes.map((route) => route.setters).flat(), "uuid");
  const distinctColors = uniq(routes.map((route) => route.colors));
  const distinctHolds = uniq(routes.map((route) => route.holds).flat());
  const distinctStyles = uniq(routes.map((route) => route.styles).flat());
  const distinctSkills = uniq(routes.map((route) => route.skills).flat());

  const qdCounts = routes.map((route) => route.quickdrawCount);
  const heights = routes.map((route) => route.height);
  const ascentOptions = routes.map(getAscentOptions);
  const names = routes.map((route) => route.name);
  const descriptions = routes.map((route) => route.description);

  return (
    <Stack sx={{ overflow: "auto" }}>
      {hasRouteWithDisplayNumber ? (
        <DetailsSection title="Number">
          <Typography fontWeight="bold">{getTruncatedDisplay(routeNumbers)}</Typography>
        </DetailsSection>
      ) : null}
      {hasRouteWithCircuit ? (
        <DetailsSection title="Circuit">
          <Typography fontWeight="bold">{getTruncatedDisplay(routeCircuitNames)}</Typography>
        </DetailsSection>
      ) : null}
      <DetailsSection title="Zone">
        <Typography fontWeight="bold">{getTruncatedDisplay(routeZoneNames)}</Typography>
      </DetailsSection>
      <DetailsSection title="Grade">
        <Typography fontWeight="bold">{getTruncatedDisplay(routeGradeValues)}</Typography>
      </DetailsSection>
      <Stack direction="row">
        <Box sx={{ flex: 1 }}>
          <DetailsSection title="Set date">
            <Typography fontWeight="bold">{singleOrPlaceholder(startDatesFormatted)}</Typography>
          </DetailsSection>
        </Box>
        <Box sx={{ flex: 1 }}>
          <DetailsSection title="Strip date">
            <Typography fontWeight="bold">{singleOrPlaceholder(endDatesFormatted)}</Typography>
          </DetailsSection>
        </Box>
      </Stack>
      <DetailsSection title="Lifetime">
        <Typography fontWeight="bold">{getTruncatedDisplay(lifetimesFormatted)}</Typography>
      </DetailsSection>
      <DetailsSection title="Setters">
        <Stack spacing={0.5}>
          {distinctSetters.map((setter) => (
            <Stack key={setter.uuid} direction="row" gap={0.5} alignItems="center">
              <Avatar user={setter} size={24} />
              <Typography fontWeight="bold">{setter.name}</Typography>
            </Stack>
          ))}
        </Stack>
      </DetailsSection>
      <DetailsSection title="Hold colors">
        <Stack>
          {Array.from(distinctColors).map((colors) => (
            <RouteCircle key={colors ?? ""} colors={(colors ?? "#FFFFFF").split(":")} />
          ))}
        </Stack>
      </DetailsSection>
      <DetailsSection title="Holds">
        <TraitsDisplay type="holds" values={distinctHolds} />
      </DetailsSection>
      <DetailsSection title="Skills">
        <TraitsDisplay type="skills" values={distinctSkills} />
      </DetailsSection>
      <DetailsSection title="Styles">
        <TraitsDisplay type="styles" values={distinctStyles} />
      </DetailsSection>
      {routes[0]?.climbType === "SPORT" ? (
        <>
          <DetailsSection title="Quickdraws">
            <Typography fontWeight="bold">{getTruncatedDisplay(qdCounts)}</Typography>
          </DetailsSection>
          <DetailsSection title="Height">
            <Typography fontWeight="bold">{getTruncatedDisplay(heights)}</Typography>
          </DetailsSection>
          <DetailsSection title="Ascent options">
            <Typography fontWeight="bold">{getTruncatedDisplay(ascentOptions)}</Typography>
          </DetailsSection>
        </>
      ) : null}
      <DetailsSection title="Name">
        <Typography fontWeight="bold">{singleOrPlaceholder(names)}</Typography>
      </DetailsSection>
      <DetailsSection title="Description">
        <Typography>{singleOrPlaceholder(descriptions)}</Typography>
      </DetailsSection>
    </Stack>
  );
});
RouteDetails.displayName = "RouteDetails";
